import React, { useState, useEffect } from 'react';
import { Building2, Plus, Edit2, Trash2, Save, X, Search, MapPin, Phone, Mail, Award, AlertTriangle } from 'lucide-react';
import { getApiUrl } from '../../config/api';
import { toast } from '../../utils/toast';

interface Institute {
  _id?: string;
  id?: string;
  name: string;
  description?: string;
  address?: string;
  contactEmail?: string;
  contactPhone?: string;
  status?: 'active' | 'inactive';
  createdAt?: string;
  subscriptionPlan?: 'trial' | 'basic' | 'premium' | 'enterprise';
  subscriptionStatus?: 'active' | 'expired' | 'suspended';
  subscriptionStartDate?: string;
  subscriptionEndDate?: string;
  licensedSeats?: number;
  billingContactEmail?: string;
}

const PLAN_COLORS: Record<string, string> = {
  trial: 'bg-gray-100 text-gray-700',
  basic: 'bg-sky-100 text-sky-700',
  premium: 'bg-violet-100 text-violet-700',
  enterprise: 'bg-amber-100 text-amber-700',
};

const getExpiryBadge = (endDate?: string) => {
  if (!endDate) return null;
  const daysLeft = Math.ceil((new Date(endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  if (daysLeft < 0) return { label: 'Expired', color: 'bg-rose-100 text-rose-700' };
  if (daysLeft <= 30) return { label: `Expires in ${daysLeft}d`, color: 'bg-amber-100 text-amber-700' };
  return null;
};

const InstituteManagement: React.FC = () => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [institutes, setInstitutes] = useState<Institute[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    address: '',
    contactEmail: '',
    contactPhone: '',
    status: 'active' as 'active' | 'inactive',
    subscriptionPlan: 'basic' as 'trial' | 'basic' | 'premium' | 'enterprise',
    subscriptionStatus: 'active' as 'active' | 'expired' | 'suspended',
    subscriptionStartDate: '',
    subscriptionEndDate: '',
    licensedSeats: 0,
    billingContactEmail: ''
  });

  useEffect(() => {
    fetchInstitutes();
  }, []);

  const fetchInstitutes = async () => {
    setLoading(true);
    try {
      const res = await fetch(getApiUrl('api/institutes'));
      if (!res.ok) throw new Error('Failed to fetch institutes');
      const data = await res.json();
      setInstitutes(data);
    } catch (err) {
      console.error('Error loading institutes:', err);
      toast('Error loading institutes');
    } finally {
      setLoading(false);
    }
  };

  const handleAddInstitute = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast('Institute name is required');
      return;
    }
    
    try {
      const res = await fetch(getApiUrl('api/institutes'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      if (!res.ok) throw new Error('Failed to add institute');
      
      const newInstitute = await res.json();
      setInstitutes(prev => [...prev, newInstitute]);
      resetForm();
      setShowAddForm(false);
    } catch (err) {
      console.error('Error adding institute:', err);
      toast('Error adding institute');
    }
  };

  const handleUpdateInstitute = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !editingId) {
      toast('Institute name is required');
      return;
    }
    
    try {
      const res = await fetch(getApiUrl(`api/institutes/${editingId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      
      if (!res.ok) throw new Error('Failed to update institute');
      
      const updated = await res.json();
      setInstitutes(prev => prev.map(inst => 
        (inst._id || inst.id) === editingId ? updated : inst
      ));
      resetForm();
      setEditingId(null);
    } catch (err) {
      console.error('Error updating institute:', err);
      toast('Error updating institute');
    }
  };

  const handleDeleteInstitute = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this institute?')) return;
    
    try {
      const res = await fetch(getApiUrl(`api/institutes/${id}`), {
        method: 'DELETE'
      });
      
      if (!res.ok) throw new Error('Failed to delete institute');
      
      setInstitutes(prev => prev.filter(inst => (inst._id || inst.id) !== id));
    } catch (err) {
      console.error('Error deleting institute:', err);
      toast('Error deleting institute');
    }
  };

  const startEdit = (institute: Institute) => {
    setEditingId(institute._id || institute.id || '');
    setFormData({
      name: institute.name,
      description: institute.description || '',
      address: institute.address || '',
      contactEmail: institute.contactEmail || '',
      contactPhone: institute.contactPhone || '',
      status: institute.status || 'active',
      subscriptionPlan: institute.subscriptionPlan || 'basic',
      subscriptionStatus: institute.subscriptionStatus || 'active',
      subscriptionStartDate: institute.subscriptionStartDate ? institute.subscriptionStartDate.slice(0, 10) : '',
      subscriptionEndDate: institute.subscriptionEndDate ? institute.subscriptionEndDate.slice(0, 10) : '',
      licensedSeats: institute.licensedSeats || 0,
      billingContactEmail: institute.billingContactEmail || ''
    });
    setShowAddForm(false);
    
    // Scroll to top after state updates
    setTimeout(() => {
      containerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      address: '',
      contactEmail: '',
      contactPhone: '',
      status: 'active',
      subscriptionPlan: 'basic',
      subscriptionStatus: 'active',
      subscriptionStartDate: '',
      subscriptionEndDate: '',
      licensedSeats: 0,
      billingContactEmail: ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setShowAddForm(false);
    resetForm();
  };

  const filteredInstitutes = institutes.filter(inst =>
    inst.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inst.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    inst.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading institutes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" ref={containerRef}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-purple-600 rounded-lg">
                <Building2 className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Institute Management</h1>
                <p className="text-gray-600 mt-1">Manage your institutes and organizations</p>
              </div>
            </div>
            <button
              onClick={() => {
                setShowAddForm(!showAddForm);
                setEditingId(null);
                resetForm();
              }}
              className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg shadow-lg transition-all duration-200 transform hover:scale-105"
            >
              <Plus className="h-5 w-5" />
              <span>Add Institute</span>
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search institutes by name, description, or address..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Add/Edit Form */}
        {(showAddForm || editingId) && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingId ? 'Edit Institute' : 'Add New Institute'}
              </h2>
              <button
                onClick={cancelEdit}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <form onSubmit={editingId ? handleUpdateInstitute : handleAddInstitute} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Institute Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="Enter institute name"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={formData.contactEmail}
                    onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="contact@institute.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contact Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.contactPhone}
                    onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    placeholder="+1 234 567 8900"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Address
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Enter institute address"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  rows={3}
                  placeholder="Enter institute description"
                />
              </div>

              <div className="pt-2 border-t border-gray-200">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 mt-3">License / Subscription</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Plan</label>
                    <select
                      value={formData.subscriptionPlan}
                      onChange={(e) => setFormData({ ...formData, subscriptionPlan: e.target.value as any })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="trial">Trial</option>
                      <option value="basic">Basic</option>
                      <option value="premium">Premium</option>
                      <option value="enterprise">Enterprise</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Subscription Status</label>
                    <select
                      value={formData.subscriptionStatus}
                      onChange={(e) => setFormData({ ...formData, subscriptionStatus: e.target.value as any })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      <option value="active">Active</option>
                      <option value="expired">Expired</option>
                      <option value="suspended">Suspended</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                    <input
                      type="date"
                      value={formData.subscriptionStartDate}
                      onChange={(e) => setFormData({ ...formData, subscriptionStartDate: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                    <input
                      type="date"
                      value={formData.subscriptionEndDate}
                      onChange={(e) => setFormData({ ...formData, subscriptionEndDate: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Licensed Seats</label>
                    <input
                      type="number"
                      min={0}
                      value={formData.licensedSeats}
                      onChange={(e) => setFormData({ ...formData, licensedSeats: parseInt(e.target.value) || 0 })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Billing Contact Email</label>
                    <input
                      type="email"
                      value={formData.billingContactEmail}
                      onChange={(e) => setFormData({ ...formData, billingContactEmail: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      placeholder="billing@institute.com"
                    />
                  </div>
                </div>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-2 rounded-lg transition-colors"
                >
                  <Save className="h-4 w-4" />
                  <span>{editingId ? 'Update Institute' : 'Create Institute'}</span>
                </button>
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="flex items-center space-x-2 bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-2 rounded-lg transition-colors"
                >
                  <X className="h-4 w-4" />
                  <span>Cancel</span>
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Institutes</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{institutes.length}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-lg">
                <Building2 className="h-8 w-8 text-purple-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Institutes</p>
                <p className="text-3xl font-bold text-green-600 mt-2">
                  {institutes.filter(i => i.status === 'active').length}
                </p>
              </div>
              <div className="p-3 bg-green-100 rounded-lg">
                <Building2 className="h-8 w-8 text-green-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Inactive Institutes</p>
                <p className="text-3xl font-bold text-gray-600 mt-2">
                  {institutes.filter(i => i.status === 'inactive').length}
                </p>
              </div>
              <div className="p-3 bg-gray-100 rounded-lg">
                <Building2 className="h-8 w-8 text-gray-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Institutes Grid */}
        {filteredInstitutes.length === 0 ? (
          <div className="bg-white rounded-xl shadow-lg p-12 text-center border border-gray-200">
            <Building2 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No institutes found</h3>
            <p className="text-gray-600 mb-6">
              {searchTerm ? 'Try adjusting your search criteria' : 'Get started by adding your first institute'}
            </p>
            {!searchTerm && (
              <button
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center space-x-2 bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg"
              >
                <Plus className="h-5 w-5" />
                <span>Add Your First Institute</span>
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredInstitutes.map((institute) => (
              <div
                key={institute._id || institute.id}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow duration-200 border border-gray-200 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Building2 className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{institute.name}</h3>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      institute.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {institute.status || 'active'}
                    </span>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${PLAN_COLORS[institute.subscriptionPlan || 'basic']}`}>
                      <Award className="h-3 w-3" />
                      {institute.subscriptionPlan || 'basic'}
                    </span>
                    {institute.licensedSeats ? (
                      <span className="text-xs text-gray-500">{institute.licensedSeats} seats</span>
                    ) : null}
                    {getExpiryBadge(institute.subscriptionEndDate) && (
                      <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getExpiryBadge(institute.subscriptionEndDate)!.color}`}>
                        <AlertTriangle className="h-3 w-3" />
                        {getExpiryBadge(institute.subscriptionEndDate)!.label}
                      </span>
                    )}
                  </div>

                  {institute.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {institute.description}
                    </p>
                  )}
                  
                  <div className="space-y-2 mb-4 pb-4 border-b border-gray-200">
                    {institute.address && (
                      <div className="flex items-start space-x-2">
                        <MapPin className="h-4 w-4 text-gray-400 mt-0.5" />
                        <span className="text-sm text-gray-600">{institute.address}</span>
                      </div>
                    )}
                    {institute.contactEmail && (
                      <div className="flex items-center space-x-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{institute.contactEmail}</span>
                      </div>
                    )}
                    {institute.contactPhone && (
                      <div className="flex items-center space-x-2">
                        <Phone className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{institute.contactPhone}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => startEdit(institute)}
                      className="flex-1 flex items-center justify-center space-x-2 bg-purple-50 hover:bg-purple-100 text-purple-700 px-4 py-2 rounded-lg transition-colors"
                    >
                      <Edit2 className="h-4 w-4" />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => handleDeleteInstitute(institute._id || institute.id || '')}
                      className="flex-1 flex items-center justify-center space-x-2 bg-red-50 hover:bg-red-100 text-red-700 px-4 py-2 rounded-lg transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InstituteManagement;
